import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Users, Loader2, CheckCircle2, Copy, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DistributorSignupModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password min 6 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/referrals/register", form);
      setDone(data);
      toast.success("You're in! Welcome to the Referral Partner Program");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(null);
    setForm({ name: "", mobile: "", email: "", city: "", password: "" });
    onOpenChange(false);
  };

  const copyLink = () => {
    if (!done?.referral_link) return;
    navigator.clipboard.writeText(done.referral_link);
    toast.success("Referral link copied");
  };

  const waShare = () => {
    if (!done?.referral_link) return;
    const text = `Try HisaabPro free for 30 days — Pakistan ka #1 Qist Manager. Sign up with my link: ${done.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md p-0 overflow-y-auto max-h-[calc(100dvh-2rem)]" data-testid="dist-signup-modal">
        <div className="bg-emerald-700 text-white px-6 py-5">
          <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase font-bold tracking-widest">
            <Users className="w-3.5 h-3.5" /> HisaabPro Referral Partner Program
          </div>
          <DialogHeader className="mt-1">
            <DialogTitle className="text-2xl font-display font-bold text-white">
              Refer &amp; Earn — Free to Join
            </DialogTitle>
            <DialogDescription className="text-emerald-50 text-sm mt-1">
              Apna unique referral link customers ke saath share karein. Jab woh <b className="text-white">PKR 1,500</b> subscription pay karte hain, aap ko har qualifying payment par <b className="text-white">PKR 500 fixed reward</b> milega.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="p-6 space-y-4 text-center" data-testid="dist-signup-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <div>
              <div className="font-display text-xl font-bold">Welcome aboard, {form.name?.split(" ")[0] || "Partner"}!</div>
              <p className="text-slate-600 text-sm mt-1">
                Aap ka Referral Partner account active hai — earning shuru karne ke liye apna link share karein.
              </p>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-left" data-testid="dist-signup-code-card">
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Your Referral Code</div>
              <div className="font-mono font-bold text-lg text-amber-900 mt-1" data-testid="dist-signup-code">{done.referral_code}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mt-2">Your Referral Link</div>
              <div className="font-mono text-xs text-amber-900 mt-1 break-all" data-testid="dist-signup-link">{done.referral_link}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={copyLink} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="dist-signup-copy">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
              <Button onClick={waShare} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-signup-wa">
                <MessageCircle className="w-4 h-4 mr-1" /> Share on WhatsApp
              </Button>
            </div>
            <Button onClick={close} variant="outline" className="w-full" data-testid="dist-signup-close">
              Login to dashboard
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3.5" data-testid="dist-signup-form">
            <div className="rounded-md bg-emerald-50 border border-emerald-100 p-3 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="text-xs text-emerald-900">
                Fixed <b>PKR 500 reward</b> on every qualifying paid subscription you refer. Free forever — no joining fee.
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ali Khan" data-testid="dist-signup-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Mobile</Label>
                <Input required inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="03001234567" data-testid="dist-signup-mobile" />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Lahore" data-testid="dist-signup-city" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="dist-signup-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="dist-signup-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-base" data-testid="dist-signup-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Join — It's Free
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
